import { useEffect, useState } from "react";
import Table from "../../../components/Table/Table";
import Loader from "../../../components/Loader/Loader";
import CardHeader from "../../../components/Card/CardHeader";
import { FaCalendarCheck } from "react-icons/fa";
import { enqueueSnackbar } from "notistack";
import { tableConstants } from "./AppointmentTableConstant";
import AppointmentReplyForm from "./AppointmentReplyForm";
import { useAppointments } from "./useAppointments";
import AppointmentFilter from "./AppointmentFilter";
import Pagination from "../../../components/Pagination/Pagination";

const AppointmentList = () => {
  const {
    appointments,
    paginationMeta,
    meetingAuthorityOptions,
    isLoader,
    fetchAppointments,
    replyToAppointment,
    closeAppointmentById,
    fetchMeetingAuthorities,
  } = useAppointments();

  const permissions = JSON.parse(localStorage.getItem("permissions"));

  // Pagination state (0-based for react-paginate, API uses 1-based)
  const [currentPage, setCurrentPage] = useState(0);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
    fetchAppointments({ ...filters, page: selected + 1 });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Filters state
   */
  const [filters, setFilters] = useState({
    status: "",
    meeting_with: "",
    from_date: "",
    to_date: "",
  });

  /**
   * Reply form state
   */
  const [formData, setFormData] = useState({
    id: "",
    meeting_date: "",
    meeting_time: "",
    meeting_mode: "",
    reply_notes: "",
    status: "",
  });

  /**
   * Load appointments initially
   */
  useEffect(() => {
    fetchAppointments({
      status: "",
      meeting_with: "",
      from_date: "",
      to_date: "",
    });
    fetchMeetingAuthorities();
  }, [fetchAppointments, fetchMeetingAuthorities]);

  /**
   * Handle filter change
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * Apply filters
   */
  const applyFilters = () => {
    setCurrentPage(0);
    fetchAppointments({ ...filters, page: 1 });
  };

  /**
   * Reset filters
   */
  const resetFilters = () => {
    const reset = {
      status: "",
      meeting_with: "",
      from_date: "",
      to_date: "",
    };

    setFilters(reset);
    setCurrentPage(0);
    fetchAppointments({ ...reset, page: 1 });
  };

  /**
   * Open reply modal
   */
  const openReplyModal = (row) => {
    setFormData({
      id: row.id,
      meeting_date: row.meeting_date || "",
      meeting_time: row.meeting_time || "",
      meeting_mode: row.meeting_mode || "",
      reply_notes: row.reply_notes || "",
      status: row.status || "",
    });

    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (
      !formData.meeting_date ||
      !formData.meeting_time ||
      !formData.meeting_mode ||
      !formData.status
    ) {
      enqueueSnackbar("Please fill all required fields", {
        variant: "warning",
      });

      return;
    }

    const success = await replyToAppointment(formData.id, formData);

    if (success) closeModal();
  };

  const closeAppointment = async (id) => {
    await closeAppointmentById(id);
  };

  const viewAppointment = (row) => openReplyModal(row);

  // Highlight if appointment is older than one day (not just 24 hours)
  const isOlderThanOneDay = (requestedAt) => {
    if (!requestedAt) return false;
    const requestDate = new Date(requestedAt);
    const now = new Date();
    // Zero out the time for both dates
    requestDate.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    const diffDays = (now - requestDate) / (1000 * 60 * 60 * 24);
    return diffDays > 1;
  };

  return (
    <>
      {isLoader && <Loader />}

      <div className="card">
        <CardHeader title="Appointment List" icon={<FaCalendarCheck />} />

        <div className="card-body">
          {/* Filters */}
          <AppointmentFilter
            filters={filters}
            onApplyFilter={applyFilters}
            onResetFilter={resetFilters}
            handleFilterChange={handleFilterChange}
            meetingAuthorityOptions={meetingAuthorityOptions}
          />

          {/* Table */}
          <Table
            cols={tableConstants(
              permissions,
              openReplyModal,
              closeAppointment,
              viewAppointment,
            )}
            data={appointments}
            getRowClassName={(rowData) => {
              const statusMatch = rowData.status === 1 || rowData.status === "ConfirmationAwaited";
              return isOlderThanOneDay(rowData.requested_at) && statusMatch
                ? "highlight-confirmation-awaited"
                : "";
            }}
          />

          {/* Pagination */}
          <Pagination
            totalPage={paginationMeta.last_page}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
          />
        </div>
      </div>

      <AppointmentReplyForm
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        formData={formData}
        handleChange={handleChange}
        handleSave={handleSave}
      />
    </>
  );
};

export default AppointmentList;
